import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "hyd_cart_v1";
const PINCODE_KEY = "hyd_pincode";
const SESSION_KEY = "hyd_cart_session";
const CONFIG_KEY = "hyd_config_v1";

const DEFAULT_CONFIG = { free_delivery_above: 2500, delivery_fee: 99 };

function getOrCreateSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (crypto?.randomUUID?.() || (Date.now() + "-" + Math.random().toString(36).slice(2)));
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

/**
 * Cart item shape:
 *   { key, kind: 'product'|'combo', ref_id, name, unit_price, mrp, image_url, qty, category_id?, is_green_cracker? }
 * key = `${kind}:${ref_id}`
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [pincode, setPincodeState] = useState(() => localStorage.getItem(PINCODE_KEY) || "");
  const [config, setConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(CONFIG_KEY);
      return raw ? { ...DEFAULT_CONFIG, ...JSON.parse(raw) } : DEFAULT_CONFIG;
    } catch { return DEFAULT_CONFIG; }
  });
  const sessionIdRef = useRef(getOrCreateSessionId());
  const debounceRef = useRef(null);

  // Fetch runtime config once so delivery threshold stays in sync with backend
  useEffect(() => {
    api.get("/config").then((r) => {
      if (r?.data) {
        const next = { ...DEFAULT_CONFIG, ...r.data };
        setConfig(next);
        localStorage.setItem(CONFIG_KEY, JSON.stringify(next));
      }
    }).catch(() => {});
  }, []);

  // Debounced sync to backend for admin observability
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const payload = {
        session_id: sessionIdRef.current,
        items: items.map((i) => ({
          kind: i.kind,
          ref_id: i.ref_id,
          name: i.name,
          unit_price: i.unit_price,
          qty: i.qty,
          image_url: i.image_url || "",
        })),
      };
      api.post("/cart/sync", payload).catch(() => {});
    }, 700);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [items]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const setPincode = (p) => {
    setPincodeState(p);
    if (p) localStorage.setItem(PINCODE_KEY, p);
    else localStorage.removeItem(PINCODE_KEY);
  };

  const getItem = useCallback((kind, refId) => items.find((i) => i.kind === kind && i.ref_id === refId), [items]);

  const addItem = useCallback((it) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.kind === it.kind && i.ref_id === it.ref_id);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], qty: cp[idx].qty + 1 };
        return cp;
      }
      return [...prev, { ...it, qty: 1, key: `${it.kind}:${it.ref_id}` }];
    });
  }, []);

  const setQty = useCallback((kind, refId, qty) => {
    setItems((prev) => {
      if (qty <= 0) return prev.filter((i) => !(i.kind === kind && i.ref_id === refId));
      return prev.map((i) => (i.kind === kind && i.ref_id === refId ? { ...i, qty } : i));
    });
  }, []);

  const removeItem = useCallback((kind, refId) => setQty(kind, refId, 0), [setQty]);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);
    const mrpTotal = items.reduce((s, i) => s + i.qty * (i.mrp || i.unit_price), 0);
    const savings = Math.max(0, mrpTotal - subtotal);
    const freeAbove = Number(config.free_delivery_above) || 2500;
    const fee = Number(config.delivery_fee) || 99;
    const deliveryFee = subtotal >= freeAbove || subtotal === 0 ? 0 : fee;
    const freeDeliveryRemaining = Math.max(0, freeAbove - subtotal);
    const total = subtotal + deliveryFee;
    return { count, subtotal, mrpTotal, savings, deliveryFee, total, freeDeliveryAbove: freeAbove, freeDeliveryRemaining };
  }, [items, config]);

  return (
    <CartContext.Provider
      value={{ items, addItem, setQty, removeItem, clear, getItem, totals, pincode, setPincode, config }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
